import { AssignationStatus, VehicleType, activeAssignationStatuses } from "@daytrip/legacy-enums";
import { isUndefinedOrNull } from "@daytrip/utils";
import { Expose, Transform, Type } from "class-transformer";
import {
    IsArray,
    IsDate,
    IsDefined,
    IsInt,
    IsNumber,
    IsOptional,
    IsString,
    IsUUID,
    ValidateIf,
    ValidateNested,
} from "class-validator";
import { v4 as uuid } from "uuid";

import type { RentalVehicle } from "../interfaces/RentalVehicle";
import { CDBaseEntity, CDColumn, CDEntity, CDObjectIdColumn } from "../orm";
import { statusGetter } from "../statusGetter";
import { transformOrderIdToBookingReference } from "../transformers/transformOrderIdToBookingReference";
import { TransformToDate } from "../transformers/transformToDate";
import { CancellationInfo } from "../types/CancellationInfo";

import { AssignationAutomaticEmail } from "./AssignationAutomaticEmail";

@CDEntity("assignations")
export class Assignation extends CDBaseEntity {
    @CDObjectIdColumn()
    @IsUUID("4")
    @IsDefined()
    @Expose()
    public _id: string = uuid();

    @CDColumn()
    @IsDefined()
    @Expose()
    @TransformToDate
    public createdAt: Date = new Date();

    @CDColumn()
    @IsUUID("4")
    @IsDefined()
    @Expose()
    public orderId: string;

    @CDColumn()
    @IsUUID("4")
    @IsOptional()
    @Expose()
    public rideId?: string;

    @CDColumn()
    @IsOptional()
    @Expose()
    @TransformToDate
    public orderDepartureAt: Date;

    @CDColumn()
    @IsOptional()
    @IsNumber()
    @Expose()
    public price?: number;

    @CDColumn()
    @IsOptional()
    @IsNumber()
    @Expose()
    public fee?: number;

    @CDColumn()
    @IsUUID("4")
    @IsDefined()
    @Expose()
    public userId: string;

    @CDColumn()
    @IsInt()
    @IsDefined()
    @Expose()
    public vehicleType: VehicleType;

    // The Assignation is accepted: if the vehicle is undefined, either acceptation note or rental vehicle is defined, and contrariwise
    @CDColumn()
    @IsUUID("4")
    @IsDefined()
    @ValidateIf(
        (a: Assignation) =>
            a.status === AssignationStatus.Accepted && !a.acceptationNote && isUndefinedOrNull(a.rentalVehicle),
    )
    @Expose()
    @Transform((value) => value ?? undefined)
    public vehicleId?: string;

    @CDColumn()
    @IsString()
    @IsDefined()
    @ValidateIf(
        (a: Assignation) =>
            a.status === AssignationStatus.Accepted && !a.vehicleId && isUndefinedOrNull(a.rentalVehicle),
    )
    @Expose()
    @Transform((value) => value ?? undefined)
    public acceptationNote?: string;

    @CDColumn()
    @Expose()
    public rentalVehicle: RentalVehicle | null = null;

    @CDColumn()
    @IsArray()
    @IsDefined()
    @Expose()
    @Type(() => String)
    public subsidyIds: Array<string> = [];

    @CDColumn()
    @IsArray()
    @IsDefined()
    @Expose()
    @Type(() => String)
    public compensationIds: Array<string> = [];

    @CDColumn()
    @IsArray()
    @IsDefined()
    @Expose()
    @Type(() => String)
    public penaltyIds: Array<string> = [];

    @CDColumn()
    @IsDate()
    @Expose()
    @TransformToDate
    public acceptedAt?: Date;

    @CDColumn()
    @IsDate()
    @Expose()
    @TransformToDate
    public declinedAt?: Date;

    @CDColumn()
    @IsDate()
    @Expose()
    @TransformToDate
    public cancelledAt?: Date;

    @CDColumn()
    @IsString()
    @Expose()
    @Transform((value) => value ?? undefined)
    public reason?: string;

    @CDColumn()
    @Expose()
    public cancellationInfo?: CancellationInfo;

    @CDColumn()
    @IsArray()
    @IsDefined()
    @ValidateNested()
    @Expose()
    @Type(() => AssignationAutomaticEmail)
    public automaticEmails: Array<AssignationAutomaticEmail> = [];

    @CDColumn()
    @IsDefined()
    @IsNumber()
    @Expose()
    public taxFeeCoefficient: number = 0;

    @CDColumn()
    @IsUUID("4")
    @IsOptional()
    @Expose()
    public invoiceId?: string | null = null;

    @CDColumn()
    @IsOptional()
    @Expose()
    public assignationOfferClaimId?: string;

    @CDColumn()
    @IsOptional()
    @Expose()
    public assignationOfferId?: string;

    @CDColumn()
    @IsOptional()
    @Expose()
    public comboAssignationOfferId?: string;

    @CDColumn()
    @Expose()
    public version: number = 0;

    public get status(): AssignationStatus {
        return statusGetter(this, Assignation, AssignationStatus);
    }

    public static statusPrototype(status: AssignationStatus): Assignation {
        const defined = new Date();

        switch (status) {
            case AssignationStatus.Cancelled:
                return {
                    cancelledAt: defined,
                } as Assignation;

            case AssignationStatus.Declined:
                return {
                    declinedAt: defined,
                } as Assignation;

            case AssignationStatus.Accepted:
                return {
                    acceptedAt: defined,
                    cancelledAt: undefined,
                    declinedAt: undefined,
                } as Assignation;

            case AssignationStatus.Pending:
            default:
                return {
                    cancelledAt: undefined,
                    declinedAt: undefined,
                    acceptedAt: undefined,
                } as Assignation;
        }
    }

    public get orderConfirmationCode(): string {
        return transformOrderIdToBookingReference(this.orderId);
    }
}
export const isAssignationActive = <T extends Pick<Assignation, "status">>(assignation: T) =>
    activeAssignationStatuses.includes(assignation.status);

export const getActiveAssignations = <T extends Pick<Assignation, "status">>(assignations: T[]) =>
    assignations.filter(isAssignationActive);
